import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { Table } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';

/** Calidad de la coincidencia dentro de un campo. Más alto, mejor. */
const TIER_NONE = 0;
const TIER_CONTAINS = 1;
const TIER_WORD_START = 2;
const TIER_PREFIX = 3;
const TIER_EXACT = 4;

/** Dónde y cómo casó la búsqueda en una fila. */
interface RowMatch {
  /** `TIER_*`: igual, empieza por, empieza palabra o contiene. */
  tier: number;
  /** Índice del campo en la lista de relevancia; el 0 es el nombre. */
  field: number;
  /** Posición de la coincidencia dentro del valor. */
  pos: number;
  /** Largo del valor: a igualdad, el más corto es el más parecido. */
  len: number;
}

/** Fila que casó por un campo que no está en la lista de relevancia. Va al final. */
const NO_MATCH: RowMatch = {
  tier: TIER_NONE,
  field: Number.MAX_SAFE_INTEGER,
  pos: Number.MAX_SAFE_INTEGER,
  len: Number.MAX_SAFE_INTEGER,
};

/** Todo lo que no es letra ni dígito cuenta como separador de palabra. */
const WORD_BOUNDARY = /[^a-z0-9]/;

/**
 * Minúsculas y sin acentos, que es como busca la gente: «azucar» tiene que
 * encontrar «Azúcar». Los espacios de más se colapsan para que un pegado desde
 * otra celda no deje de casar por un doble espacio.
 */
function normalize(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Califica un valor ya normalizado contra la consulta ya normalizada. */
function scoreValue(value: string, query: string): { tier: number; pos: number } {
  if (!value) {
    return { tier: TIER_NONE, pos: -1 };
  }
  if (value === query) {
    return { tier: TIER_EXACT, pos: 0 };
  }
  const first = value.indexOf(query);
  if (first < 0) {
    return { tier: TIER_NONE, pos: -1 };
  }
  if (first === 0) {
    return { tier: TIER_PREFIX, pos: 0 };
  }
  // «trigo» empieza palabra en «Harina de trigo» y no en «Trigonometría»; la
  // primera merece salir antes, así que se recorren todas las apariciones.
  for (let at = first; at >= 0; at = value.indexOf(query, at + 1)) {
    if (WORD_BOUNDARY.test(value.charAt(at - 1))) {
      return { tier: TIER_WORD_START, pos: at };
    }
  }
  return { tier: TIER_CONTAINS, pos: first };
}

/**
 * Mejor coincidencia de la fila. El empate de calidad lo gana el campo que va
 * antes en la lista —el nombre, por cómo se ordena la lista— porque el bucle
 * recorre los campos en orden y sólo mejora con un `tier` estrictamente mayor.
 */
function matchRow(row: any, fields: string[], query: string): RowMatch {
  let best = NO_MATCH;
  for (let i = 0; i < fields.length; i++) {
    const value = normalize(ObjectUtils.resolveFieldData(row, fields[i]));
    const { tier, pos } = scoreValue(value, query);
    if (tier > best.tier) {
      best = { tier, field: i, pos, len: value.length };
    }
  }
  return best;
}

function compareMatches(a: RowMatch, b: RowMatch): number {
  if (a.tier !== b.tier) {
    return b.tier - a.tier;
  }
  if (a.field !== b.field) {
    return a.field - b.field;
  }
  if (a.pos !== b.pos) {
    return a.pos - b.pos;
  }
  return a.len - b.len;
}

/**
 * Mientras hay algo escrito en el buscador de la tabla manda la coincidencia,
 * no el orden anterior.
 *
 * El problema que resuelve: un `p-table` con `sortField` por defecto —o con la
 * columna que el usuario ordenó hace rato— sigue ordenando por esa columna
 * después de filtrar, porque PrimeNG ordena el array y filtrar sólo lo recorta.
 * Buscar «harina» en un catálogo ordenado por número de recetas devuelve las
 * coincidencias repartidas por toda la lista, con la que menos se parece
 * arriba. Al escribir, lo que importa es el parecido; el orden de antes, no.
 *
 * Qué hace, entonces, en cuanto hay texto en el filtro global:
 *
 *  1. **Quita el orden vigente** (y su flecha de la cabecera), sin tocar el
 *     array del componente: al limpiar la búsqueda vuelve solo.
 *  2. **Reordena lo filtrado por coincidencia**: primero la calidad —igual,
 *     empieza por, empieza palabra, contiene—, y a igualdad de calidad el campo
 *     que va antes en la lista de relevancia, que por convenio es el nombre.
 *     Después, la coincidencia más al principio y el valor más corto.
 *  3. **Se aparta si el usuario ordena una columna** con la búsqueda puesta:
 *     eso es una petición explícita y manda hasta que limpie el buscador. Ese
 *     orden elegido a mano se queda también después de limpiar.
 *
 * Al vaciar el buscador se devuelve el orden que había antes de escribir: el
 * `sortField` por defecto de la pantalla, o la columna que el usuario tuviera
 * ordenada.
 *
 * Sin valor usa los `globalFilterFields` de la tabla tal cual. Se le pasa una
 * lista propia cuando ese orden no empieza por el nombre —una tabla que filtra
 * por ID primero, por ejemplo— o cuando el filtro global va contra un campo
 * concatenado que no sirve para medir parecido:
 *
 * ```html
 * <p-table [globalFilterFields]="['id','nombre']"
 *          appSearchRelevance="nombre,id"> … </p-table>
 * ```
 *
 * Una fila que casó por un campo fuera de la lista de relevancia no se pierde:
 * cae al final, en el orden en que venía.
 */
@Directive({
  selector: 'p-table[appSearchRelevance]',
  standalone: true,
})
export class SearchRelevanceDirective implements OnInit, OnDestroy {
  /**
   * Campos por los que se mide el parecido, de más a menos importante. Acepta
   * un arreglo o una lista separada por comas. Vacío: `globalFilterFields`.
   */
  @Input('appSearchRelevance') searchRelevanceFields: string | string[] | undefined;

  private readonly subscriptions = new Subscription();

  /** `Table.sort` original, para devolverlo al destruir la directiva. */
  private nativeSort?: (event: any) => void;

  /** Orden vigente cuando empezó la búsqueda; se devuelve al limpiarla. */
  private sortBeforeSearch: { field: string | null | undefined; order: number } | null = null;

  /** Hay texto en el buscador. */
  private searching = false;

  /** El usuario ordenó una columna con la búsqueda puesta: manda él. */
  private pinnedByUser = false;

  constructor(private table: Table) {}

  ngOnInit(): void {
    const table = this.table;
    const nativeSort = table.sort.bind(table);
    this.nativeSort = nativeSort;
    // `Table.sort()` lo llama únicamente `pSortableColumn`, al clic o al Enter
    // sobre la cabecera: es la señal exacta de «este orden lo pido yo». Ni
    // `onSort` ni `sortSource$` sirven, porque los emite igual el orden por
    // defecto de la pantalla y el que esta directiva quita y devuelve.
    table.sort = (event: any) => {
      this.pinnedByUser = true;
      nativeSort(event);
    };
    this.subscriptions.add(table.onFilter.subscribe((event: any) => this.onFilter(event)));
  }

  ngOnDestroy(): void {
    if (this.nativeSort) {
      this.table.sort = this.nativeSort;
    }
    this.subscriptions.unsubscribe();
  }

  private onFilter(event: any): void {
    // Ordenación múltiple: el criterio es una lista de columnas y quitarla a
    // medias deja la tabla en un estado que el usuario no pidió. Fuera.
    if (this.table.sortMode !== 'single') {
      return;
    }

    const query = normalize(event?.filters?.['global']?.value);
    if (!query) {
      this.endSearch();
      return;
    }

    if (!this.searching) {
      this.searching = true;
      this.pinnedByUser = false;
      this.sortBeforeSearch = { field: this.table.sortField, order: this.table.sortOrder };
    }

    if (this.pinnedByUser) {
      return;
    }

    this.dropSort();
    this.rankByRelevance(query);
  }

  /** Quita el orden vigente y apaga la flecha de la cabecera. */
  private dropSort(): void {
    const table = this.table;
    if (table.sortField === null || table.sortField === undefined) {
      return;
    }
    table.sortField = null;
    table.sortOrder = 1;
    // Sin esto la columna anterior se queda pintada como ordenada aunque la
    // tabla ya no lo esté: `pSortableColumn` sólo se repinta con este aviso.
    table.tableService.onSort(null);
  }

  /**
   * Reordena por parecido lo que la tabla pinta.
   *
   * Reordena una copia y no el array del componente a propósito: ese array es
   * el que vuelve a verse al limpiar el buscador, y su orden es el que hay que
   * devolver intacto.
   */
  private rankByRelevance(query: string): void {
    const table = this.table;
    const fields = this.relevanceFields();
    const rows = table.filteredValue ?? table.value;
    if (!fields.length || !rows || !rows.length) {
      return;
    }

    // Las puntuaciones se calculan una vez por fila; dentro del comparador se
    // recalcularían O(n log n) veces, con `resolveFieldData` y `normalize` de
    // por medio en cada tecleo.
    const scores = new Map<any, RowMatch>();
    for (const row of rows) {
      scores.set(row, matchRow(row, fields, query));
    }

    // `sort` es estable: las filas igual de parecidas —y las que casaron por un
    // campo fuera de la lista— conservan el orden en el que venían.
    table.filteredValue = [...rows].sort((a, b) =>
      compareMatches(scores.get(a) ?? NO_MATCH, scores.get(b) ?? NO_MATCH),
    );
  }

  private relevanceFields(): string[] {
    const declared = this.searchRelevanceFields;
    if (Array.isArray(declared)) {
      return declared.filter((field) => !!field);
    }
    if (typeof declared === 'string' && declared.trim()) {
      return declared
        .split(',')
        .map((field) => field.trim())
        .filter((field) => !!field);
    }
    return (this.table.globalFilterFields ?? []).filter((field) => !!field);
  }

  /** Buscador vacío: se devuelve el orden que había antes de escribir. */
  private endSearch(): void {
    if (!this.searching) {
      return;
    }
    const previous = this.sortBeforeSearch;
    const pinned = this.pinnedByUser;
    this.searching = false;
    this.sortBeforeSearch = null;
    this.pinnedByUser = false;

    // Si eligió columna con la búsqueda puesta, ése es ahora su orden: se queda
    // en vez del que había antes de escribir.
    if (pinned || !previous || !previous.field) {
      return;
    }
    this.table.sortField = previous.field;
    this.table.sortOrder = previous.order;
    this.table.sortSingle();
  }
}
